// src/components/ExistingWorkspace.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './templateWorkspace.css'; // Shared CSS

import Sidebar from '../../myDashboard/Sidebar';
import CloseSidebar from '../../myDashboard/CloseSidebar';
import AppTransition from '../../components/appComponents/AppTransition';
import WorkspaceHeader from './workspaceUploads/WorkspaceHeader';
import UploadWorkspace from './workspaceUploads/uploadWorkspace';
import ShortcutsWorkspace from './shortcutsWorkspace';
import PromptsWorkspace from './promptBox/promptsWorkspace';
import ProcessingPage from '../../unAuthPages/ProcessingPage';

import { updateWorkspace } from './updateWorkspace';

// NEW: import listener manager and popup components
import { registerListener } from '../../components/appComponents/ListenerManager';
import InsufficientTimePopUp from '../../components/miscComponents/InsufficientTimePopUp';



const ExistingWorkspace = ({ isSidebarOpen, toggleSidebar }) => {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const { content_id: rawContentId } = useParams();
  const content_id = rawContentId?.startsWith('workspace-')
    ? rawContentId.replace(/^workspace-/, '')
    : rawContentId;


  // NEW: state for insufficient-time popup
  const [insufficientTime, setInsufficientTime] = useState(false);

  //
  // ─── POPUP SUBSCRIPTION ────────────────────────────────────────────────────────────
  //
  useEffect(() => {
    if (!user) return;

    const noTimeDocRef = doc(
      db,
      'users', user.uid,
      'subscriptionData', 'noTimeAvailable'
    );
    const unsubscribe = onSnapshot(noTimeDocRef, snap => {
      const flag = snap.exists() && snap.data().insufficientTime;
      setInsufficientTime(flag);
    });

    registerListener(unsubscribe);
    return () => unsubscribe();
  }, [user]);




  //
  // ─── METADATA STATE ─────────────────────────────────────────────────────────────--
  //
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [isScTimestampsEnabled, setIsScTimestampsEnabled] = useState(false);
  const [isScDescriptionEnabled, setIsScDescriptionEnabled] = useState(false);
  const [isScTakeawaysEnabled, setIsScTakeawaysEnabled] = useState(false);
  const [isScTitlesEnabled, setIsScTitlesEnabled] = useState(false);
  const [isScTagsEnabled, setIsScTagsEnabled] = useState(false);
  const [isTranscriptAvailable, setIsTranscriptAvailable] = useState(false);
  const [isLocalTranscriptAvailable, setIsLocalTranscriptAvailable] = useState(false);
  const [isYTProcessing, setIsYTProcessing] = useState(false);
  const [isLOCALProcessing, setIsLOCALProcessing] = useState(false);
  const [isTranscriptError, setIsTranscriptError] = useState(false);
  const [isTranscriptEmpty, setIsTranscriptEmpty] = useState(false);

  const [timestamps, setTimestamps] = useState('');
  const [summary, setSummary] = useState('');
  const [takeaways, setTakeaways] = useState([]);
  const [titles, setTitles] = useState([]);
  const [hashtags, setHashtags] = useState('');
  const [isScRun, setisScRun] = useState(false);

  // originals for metadata
  const [originalWorkspaceTitle, setOriginalWorkspaceTitle] = useState('');
  const [originalIsScTimestampsEnabled, setOriginalIsScTimestampsEnabled] = useState(false);
  const [originalIsScDescriptionEnabled, setOriginalIsScDescriptionEnabled] = useState(false);
  const [originalIsScTakeawaysEnabled, setOriginalIsScTakeawaysEnabled] = useState(false);
  const [originalIsScTitlesEnabled, setOriginalIsScTitlesEnabled] = useState(false);
  const [originalIsScTagsEnabled, setOriginalIsScTagsEnabled] = useState(false);
  const [originalIsTranscriptAvailable, setOriginalIsTranscriptAvailable] = useState(false);
  const [originalIsLocalTranscriptAvailable, setOriginalIsLocalTranscriptAvailable] = useState(false);
  const [originalIsYTProcessing, setOriginalIsYTProcessing] = useState(false);
  const [originalIsLOCALProcessing, setOriginalIsLOCALProcessing] = useState(false);
  const [originalIsTranscriptError, setOriginalIsTranscriptError] = useState(false);
  const [originalIsTranscriptEmpty, setOriginalIsTranscriptEmpty] = useState(false);

  // originals for chat maps
  const [originalPromptMap, setOriginalPromptMap] = useState({});
  const [originalOutputMap, setOriginalOutputMap] = useState({});
  const [originalChatNameMap, setOriginalChatNameMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);

  //
  // ─── CHAT-MAP STATE (lifted) ────────────────────────────────────────────────────
  //
  const [promptMap, setPromptMap] = useState({ "0": "" });
  const [outputMap, setOutputMap] = useState({ "0": "" });
  const [chatNameMap, setChatNameMap] = useState({ "0": "New Chat" });
  // new: processing status per chat
  // if absent, treat as not processing
  const [processingMap, setProcessingMap] = useState({});

  // hydrate & subscribe to Firestore/localStorage
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'workspaceContent', `workspace-${content_id}`);

    // initial load
    (async () => {
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          const pm   = data.prompt_map   ?? { "0": "" };
          const om   = data.output_map   ?? { "0": "" };
          const cnm  = data.chat_name_map?? { "0": "New Chat" };
          const pr   = data.processing_map ?? {};

          setPromptMap(pm);
          setOutputMap(om);
          setChatNameMap(cnm);
          setProcessingMap(pr);

          // sync originals
          setOriginalPromptMap(pm);
          setOriginalOutputMap(om);
          setOriginalChatNameMap(cnm);
        } else {
          const pmLS  = JSON.parse(localStorage.getItem(`prompt_map_${content_id}`) || '{}');
          const omLS  = JSON.parse(localStorage.getItem(`output_map_${content_id}`) || '{}');
          const cnmLS = JSON.parse(localStorage.getItem(`chat_name_map_${content_id}`) || '{}');
          const prLS  = JSON.parse(localStorage.getItem(`processing_map_${content_id}`) || '{}');

          const pm  = Object.keys(pmLS).length  ? pmLS  : { "0": "" };
          const om  = Object.keys(omLS).length  ? omLS  : { "0": "" };
          const cnm = Object.keys(cnmLS).length ? cnmLS : { "0": "New Chat" };
          const pr  = Object.keys(prLS).length  ? prLS  : {};

          setPromptMap(pm);
          setOutputMap(om);
          setChatNameMap(cnm);
          setProcessingMap(pr);

          setOriginalPromptMap(pm);
          setOriginalOutputMap(om);
          setOriginalChatNameMap(cnm);
        }
      } catch (e) {
        // console.error('Error loading chat-maps:', e);
      }
    })();

    const unsub = onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        const pm  = data.prompt_map   ?? {};
        const om  = data.output_map   ?? {};
        const cnm = data.chat_name_map?? {};
        const pr  = data.processing_map ?? {};

        setPromptMap(pm);
        setOutputMap(om);
        setChatNameMap(cnm);
        setProcessingMap(pr);

        // sync originals
        setOriginalPromptMap(pm);
        setOriginalOutputMap(om);
        setOriginalChatNameMap(cnm);
      }
    });

    return () => unsub();
  }, [content_id, user]);

  // persist to localStorage on every change
  useEffect(() => {
    localStorage.setItem(`prompt_map_${content_id}`, JSON.stringify(promptMap));
    localStorage.setItem(`output_map_${content_id}`, JSON.stringify(outputMap));
    localStorage.setItem(`chat_name_map_${content_id}`, JSON.stringify(chatNameMap));
    localStorage.setItem(`processing_map_${content_id}`, JSON.stringify(processingMap));
  }, [promptMap, outputMap, chatNameMap, processingMap, content_id]);

  //
  // ─── METADATA HYDRATION ─────────────────────────────────────────────────────────
  //
  useEffect(() => {
    if (content_id && user) {
      const userDocRef     = doc(db, 'users', user.uid);
      const workspaceDocRef= doc(userDocRef, 'workspaceContent', `workspace-${content_id}`);
      const localDataKey   = `workspace-${content_id}-data`;
      const localDataJSON  = localStorage.getItem(localDataKey);

      if (localDataJSON) {
        const workspaceData = JSON.parse(localDataJSON);
        setWorkspaceTitle(workspaceData.workspace_title || '');
        setIsScTimestampsEnabled(workspaceData.is_sc_timestamps_enabled || false);
        setIsScDescriptionEnabled(workspaceData.is_sc_description_enabled || false);
        setIsScTakeawaysEnabled(workspaceData.is_sc_takeaways_enabled || false);
        setIsScTitlesEnabled(workspaceData.is_sc_titles_enabled || false);
        setIsScTagsEnabled(workspaceData.is_sc_tags_enabled || false);
        setIsTranscriptAvailable(workspaceData.is_transcript_available || false);
        setIsLocalTranscriptAvailable(workspaceData.is_local_transcript_available || false);
        setIsYTProcessing(workspaceData.is_caption_processing || false);
        setIsLOCALProcessing(workspaceData.is_local_caption_processing || false);
        setIsTranscriptError(workspaceData.is_transcript_error || false);
        setIsTranscriptEmpty(workspaceData.is_transcript_empty || false);

        setTimestamps(workspaceData.timestamps || '');
        setSummary(workspaceData.summary || '');
        setTakeaways(workspaceData.takeaways || []);
        setTitles(workspaceData.titles || []);
        setHashtags(workspaceData.hashtags || '');
        setisScRun(workspaceData.is_sc_run || false);

        setLoading(false);
      }

      const unsubscribeMeta = onSnapshot(
        workspaceDocRef,
        docSnapshot => {
          if (docSnapshot.exists()) {
            const workspaceData = docSnapshot.data();
            setWorkspaceTitle(workspaceData.workspace_title || '');
            setIsScTimestampsEnabled(workspaceData.is_sc_timestamps_enabled || false);
            setIsScDescriptionEnabled(workspaceData.is_sc_description_enabled || false);
            setIsScTakeawaysEnabled(workspaceData.is_sc_takeaways_enabled || false);
            setIsScTitlesEnabled(workspaceData.is_sc_titles_enabled || false);
            setIsScTagsEnabled(workspaceData.is_sc_tags_enabled || false);
            setIsTranscriptAvailable(workspaceData.is_transcript_available || false);
            setIsLocalTranscriptAvailable(workspaceData.is_local_transcript_available || false);
            setIsYTProcessing(workspaceData.is_caption_processing || false);
            setIsLOCALProcessing(workspaceData.is_local_caption_processing || false);
            setIsTranscriptError(workspaceData.is_transcript_error || false);
            setIsTranscriptEmpty(workspaceData.is_transcript_empty || false);

            setTimestamps(workspaceData.timestamps || '');
            setSummary(workspaceData.summary || '');
            setTakeaways(workspaceData.takeaways || []);
            setTitles(workspaceData.titles || []);
            setHashtags(workspaceData.hashtags || '');
            setisScRun(workspaceData.is_sc_run || false);

            // sync originals for metadata
            setOriginalWorkspaceTitle(workspaceData.workspace_title || '');
            setOriginalIsScTimestampsEnabled(workspaceData.is_sc_timestamps_enabled || false);
            setOriginalIsScDescriptionEnabled(workspaceData.is_sc_description_enabled || false);
            setOriginalIsScTakeawaysEnabled(workspaceData.is_sc_takeaways_enabled || false);
            setOriginalIsScTitlesEnabled(workspaceData.is_sc_titles_enabled || false);
            setOriginalIsScTagsEnabled(workspaceData.is_sc_tags_enabled || false);
            setOriginalIsTranscriptAvailable(workspaceData.is_transcript_available || false);
            setOriginalIsLocalTranscriptAvailable(workspaceData.is_local_transcript_available || false);
            setOriginalIsYTProcessing(workspaceData.is_caption_processing || false);
            setOriginalIsLOCALProcessing(workspaceData.is_local_caption_processing || false);
            setOriginalIsTranscriptError(workspaceData.is_transcript_error || false);
            setOriginalIsTranscriptEmpty(workspaceData.is_transcript_empty || false);

            localStorage.setItem(localDataKey, JSON.stringify(workspaceData));
          } else {
            localStorage.removeItem(localDataKey);
            navigate('/404-workspace');
          }
          setLoading(false);
        },
        () => setLoading(false)
      );

      return () => unsubscribeMeta();
    } else {
      setLoading(false);
    }
  }, [content_id, user, navigate]);

  //
  // ─── SAVE-BUTTON ENABLE/CHANGE DETECTION ─────────────────────────────────────────/
  //
  useEffect(() => {
    const localDataKey = `workspace-${content_id}-data`;
    const workspaceData = {
      workspace_title: workspaceTitle,
      is_sc_timestamps_enabled: isScTimestampsEnabled,
      is_sc_description_enabled: isScDescriptionEnabled,
      is_sc_takeaways_enabled: isScTakeawaysEnabled,
      is_sc_titles_enabled: isScTitlesEnabled,
      is_sc_tags_enabled: isScTagsEnabled,
      is_transcript_available: isTranscriptAvailable,
      is_local_transcript_available: isLocalTranscriptAvailable,
      is_caption_processing: isYTProcessing,
      is_local_caption_processing: isLOCALProcessing,
      is_transcript_error: isTranscriptError,
      is_transcript_empty: isTranscriptEmpty,
      timestamps,
      summary,
      takeaways,
      titles,
      hashtags,
      is_sc_run: isScRun,
    };

    // metadata changes?
    const metaChanged =
      workspaceTitle !== originalWorkspaceTitle ||
      isScTimestampsEnabled !== originalIsScTimestampsEnabled ||
      isScDescriptionEnabled !== originalIsScDescriptionEnabled ||
      isScTakeawaysEnabled !== originalIsScTakeawaysEnabled ||
      isScTitlesEnabled !== originalIsScTitlesEnabled ||
      isScTagsEnabled !== originalIsScTagsEnabled ||
      isTranscriptAvailable !== originalIsTranscriptAvailable ||
      isLocalTranscriptAvailable !== originalIsLocalTranscriptAvailable ||
      isYTProcessing !== originalIsYTProcessing ||
      isLOCALProcessing !== originalIsLOCALProcessing ||
      isTranscriptError !== originalIsTranscriptError ||
      isTranscriptEmpty !== originalIsTranscriptEmpty;

    // chat-map changes?
    const promptChanged    = JSON.stringify(promptMap)    !== JSON.stringify(originalPromptMap);
    const outputChanged    = JSON.stringify(outputMap)    !== JSON.stringify(originalOutputMap);
    const chatNamesChanged = JSON.stringify(chatNameMap)  !== JSON.stringify(originalChatNameMap);

    setIsSaveButtonEnabled(metaChanged || promptChanged || outputChanged || chatNamesChanged);

    localStorage.setItem(localDataKey, JSON.stringify(workspaceData));
  }, [
    workspaceTitle,
    isScTimestampsEnabled,
    isScDescriptionEnabled,
    isScTakeawaysEnabled,
    isScTitlesEnabled,
    isScTagsEnabled,
    isTranscriptAvailable,
    isLocalTranscriptAvailable,
    isYTProcessing,
    isLOCALProcessing,
    isTranscriptError,
    isTranscriptEmpty,
    timestamps,
    summary,
    takeaways,
    titles,
    hashtags,
    isScRun,
    promptMap,
    outputMap,
    chatNameMap,
    // originals
    originalWorkspaceTitle,
    originalIsScTimestampsEnabled,
    originalIsScDescriptionEnabled,
    originalIsScTakeawaysEnabled,
    originalIsScTitlesEnabled,
    originalIsScTagsEnabled,
    originalIsTranscriptAvailable,
    originalIsLocalTranscriptAvailable,
    originalIsYTProcessing,
    originalIsLOCALProcessing,
    originalIsTranscriptError,
    originalIsTranscriptEmpty,
    originalPromptMap,
    originalOutputMap,
    originalChatNameMap,
    content_id,
  ]);

  //
  // ─── SAVE HANDLER ───────────────────────────────────────────────────────────────
  //
  const handleUpdateWorkspace = useCallback(async () => {
    const payload = {
      user,
      content_id,
      workspaceTitle,
      isSCTimestampsEnabled: isScTimestampsEnabled,
      isSCDescriptionEnabled: isScDescriptionEnabled,
      isSCTakeawaysEnabled: isScTakeawaysEnabled,
      isSCTitlesEnabled: isScTitlesEnabled,
      isSCTagsEnabled: isScTagsEnabled,
      isTranscriptAvailable,
      isLocalTranscriptAvailable,
      isYTProcessing,
      isLOCALProcessing,
      isTranscriptError,
      isTranscriptEmpty,
      timestamps,
      summary,
      takeaways,
      titles,
      hashtags,
      isScRun,
      promptMap,
      chatNameMap,
      outputMap,
      processingMap,
    };

    // console.log('🛰️ updateWorkspace payload:', payload);

    try {
      await updateWorkspace(payload);

      // sync originals on success
      setOriginalWorkspaceTitle(workspaceTitle);
      setOriginalIsScTimestampsEnabled(isScTimestampsEnabled);
      setOriginalIsScDescriptionEnabled(isScDescriptionEnabled);
      setOriginalIsScTakeawaysEnabled(isScTakeawaysEnabled);
      setOriginalIsScTitlesEnabled(isScTitlesEnabled);
      setOriginalIsScTagsEnabled(isScTagsEnabled);
      setOriginalIsTranscriptAvailable(isTranscriptAvailable);
      setOriginalIsLocalTranscriptAvailable(isLocalTranscriptAvailable);
      setOriginalIsYTProcessing(isYTProcessing);
      setOriginalIsLOCALProcessing(isLOCALProcessing);
      setOriginalIsTranscriptError(isTranscriptError);
      setOriginalIsTranscriptEmpty(isTranscriptEmpty);

      setOriginalPromptMap(promptMap);
      setOriginalOutputMap(outputMap);
      setOriginalChatNameMap(chatNameMap);
    } catch (error) {
      // console.error('Error updating workspace:', error);
    }
  }, [
    user,
    content_id,
    workspaceTitle,
    isScTimestampsEnabled,
    isScDescriptionEnabled,
    isScTakeawaysEnabled,
    isScTitlesEnabled,
    isScTagsEnabled,
    isTranscriptAvailable,
    isLocalTranscriptAvailable,
    isYTProcessing,
    isLOCALProcessing,
    isTranscriptError,
    isTranscriptEmpty,
    timestamps,
    summary,
    takeaways,
    titles,
    hashtags,
    isScRun,
    promptMap,
    outputMap,
    chatNameMap,
    processingMap,
  ]);


  // Pop up if user reloads or exits
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isSaveButtonEnabled) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSaveButtonEnabled]);

  //
  // ─── RENDER ─────────────────────────────────────────────────────────────────────
  //
  return (
    <div className="template-workspace-container">

      {insufficientTime && <InsufficientTimePopUp />}

      {isSidebarOpen ? <Sidebar /> : <CloseSidebar toggleSidebar={toggleSidebar} />}
      <AppTransition>
        <div className="template-workspace-main">
          {loading ? (
            <ProcessingPage />
          ) : (
            <>
              <div className="workspace-header-container">
                <WorkspaceHeader
                  workspaceTitle={workspaceTitle}
                  isTranscriptAvailable={isTranscriptAvailable}
                  isLocalTranscriptAvailable={isLocalTranscriptAvailable}
                  isTranscriptError={isTranscriptError}
                  isTranscriptEmpty={isTranscriptEmpty}
                  isYTProcessing={isYTProcessing}
                  isLOCALProcessing={isLOCALProcessing}
                  workspaceCreatedFunction={handleUpdateWorkspace}
                  setWorkspaceTitle={setWorkspaceTitle}
                  isSaveButtonEnabled={isSaveButtonEnabled}
                />
              </div>

              <div className="template-row-container">
                <div className="template-upload-container">
                  <UploadWorkspace
                    isTranscriptAvailable={isTranscriptAvailable}
                    isLocalTranscriptAvailable={isLocalTranscriptAvailable}
                    isTranscriptEmpty={isTranscriptEmpty}
                    isYTProcessing={isYTProcessing}
                    isLOCALProcessing={isLOCALProcessing}
                    content_id={content_id}
                    isTranscriptError={isTranscriptError}
                  />
                  <div className="modify-shortcuts-container">
                    <ShortcutsWorkspace
                      isScTimestampsEnabled={isScTimestampsEnabled}
                      setIsScTimestampsEnabled={setIsScTimestampsEnabled}
                      isScDescriptionEnabled={isScDescriptionEnabled}
                      setIsScDescriptionEnabled={setIsScDescriptionEnabled}
                      isScTakeawaysEnabled={isScTakeawaysEnabled}
                      setIsScTakeawaysEnabled={setIsScTakeawaysEnabled}
                      isScTitlesEnabled={isScTitlesEnabled}
                      setIsScTitlesEnabled={setIsScTitlesEnabled}
                      isScTagsEnabled={isScTagsEnabled}
                      setIsScTagsEnabled={setIsScTagsEnabled}
                      isTranscriptAvailable={isTranscriptAvailable}
                      isLocalTranscriptAvailable={isLocalTranscriptAvailable}
                      content_id={content_id}
                      timestamps={timestamps}
                      summary={summary}
                      takeaways={takeaways}
                      titles={titles}
                      hashtags={hashtags}
                    />
                  </div>
                </div>
              </div>

              <div className="template-prompts-container">
                <PromptsWorkspace
                  isTranscriptAvailable={isTranscriptAvailable}
                  isLocalTranscriptAvailable={isLocalTranscriptAvailable}
                  isYTProcessing={isYTProcessing}
                  isLOCALProcessing={isLOCALProcessing}
                  content_id={content_id}
                  timestamps={timestamps}
                  summary={summary}
                  takeaways={takeaways}
                  titles={titles}
                  hashtags={hashtags}
                  isScRun={isScRun}
                  isScTimestampsEnabled={isScTimestampsEnabled}
                  isScDescriptionEnabled={isScDescriptionEnabled}
                  isScTakeawaysEnabled={isScTakeawaysEnabled}
                  isScTitlesEnabled={isScTitlesEnabled}
                  isScTagsEnabled={isScTagsEnabled}
                  promptMap={promptMap}
                  outputMap={outputMap}
                  chatNameMap={chatNameMap}
                  processingMap={processingMap}
                  setProcessingMap={setProcessingMap}
                  setPromptMap={setPromptMap}
                  setOutputMap={setOutputMap}
                  setChatNameMap={setChatNameMap}
                />
              </div>
            </>
          )}
        </div>
      </AppTransition>
    </div>
  );
};

export default ExistingWorkspace;
